import type { BaldeDeHora } from '../../api/visao-geral.js';
import { formatarHora } from '../../componentes/datas.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeMovimentacoes } from '../../componentes/Icones.js';
import './GraficoOperacoes.css';

/** Altura da coluna mais alta, em px. As demais são proporcionais a ela. */
const ALTURA_MAXIMA = 148;

/**
 * A partir daqui os rótulos de hora se encostam: a coluna tem cerca de 30px e
 * "10:00" não cabe. Mostrar um a cada duas horas mantém o eixo legível sem
 * esconder nenhuma barra — e a tabela de leitor de tela continua com todas.
 */
const BALDES_ATE_ROTULAR_TODOS = 12;

const SERIES = [
  { chave: 'coleta', classe: 'coleta', rotulo: 'Coleta' },
  { chave: 'contagemInventario', classe: 'inventario', rotulo: 'Inventário' },
  { chave: 'movimentacao', classe: 'movimentacao', rotulo: 'Movimentação' },
] as const;

function altura(valor: number, maximo: number): string {
  return `${String(Math.round((valor / maximo) * ALTURA_MAXIMA))}px`;
}

function totalDoBalde(balde: BaldeDeHora): number {
  return balde.coleta + balde.movimentacao + balde.contagemInventario;
}

/**
 * Operações por hora, três séries lado a lado.
 *
 * É um gráfico de leitura, não de análise: a pergunta que ele responde é "a
 * operação está funcionando agora?". Por isso não há eixo Y — a comparação
 * entre horas basta, e os números exatos vivem nas listagens.
 *
 * Armazém parado é um resultado, não a ausência de um: sem nenhuma operação na
 * janela, o gráfico DIZ isso. Barras todas no zero seriam uma faixa em branco
 * indistinguível de "a tela não carregou", e é justamente no turno vazio que
 * alguém precisa saber que ninguém coletou nada.
 */
export function GraficoOperacoes({ baldes }: { readonly baldes: readonly BaldeDeHora[] }) {
  const total = baldes.reduce((soma, balde) => soma + totalDoBalde(balde), 0);

  if (total === 0) {
    return (
      <EstadoVazio
        tom="neutro"
        icone={<IconeMovimentacoes tamanho={28} />}
        titulo="Nenhuma operação registrada nesta janela."
        detalhe="Nenhuma coleta, movimentação ou contagem chegou do chão de fábrica no período."
      />
    );
  }

  const maximo = Math.max(
    1,
    ...baldes.map((balde) => Math.max(...SERIES.map((serie) => balde[serie.chave]))),
  );
  const passo = baldes.length > BALDES_ATE_ROTULAR_TODOS ? 2 : 1;

  return (
    <div className="grafico">
      <div className="grafico__legenda">
        {SERIES.map((serie) => (
          <span key={serie.chave} className="grafico__item-legenda">
            <span className={`grafico__amostra grafico__amostra--${serie.classe}`} />
            {serie.rotulo}
          </span>
        ))}
      </div>

      <div className="grafico__area">
        {baldes.map((balde) => (
          <div key={balde.inicio} className="grafico__coluna">
            {SERIES.map((serie) => (
              <span
                key={serie.chave}
                className={`grafico__barra grafico__barra--${serie.classe}`}
                style={{ height: altura(balde[serie.chave], maximo) }}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="grafico__rotulos">
        {baldes.map((balde, indice) => (
          <span key={balde.inicio} className="grafico__rotulo mono">
            {indice % passo === 0 ? formatarHora(balde.inicio) : ''}
          </span>
        ))}
      </div>

      <table className="apenas-leitor-de-tela">
        <caption>Operações por hora</caption>
        <thead>
          <tr>
            <th scope="col">Hora</th>
            {SERIES.map((serie) => (
              <th key={serie.chave} scope="col">
                {serie.rotulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {baldes.map((balde) => (
            <tr key={balde.inicio}>
              <th scope="row">{formatarHora(balde.inicio)}</th>
              {SERIES.map((serie) => (
                <td key={serie.chave}>{balde[serie.chave]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
